import React, { useEffect, useState } from 'react'
import SEO from '../components/SEO'
import apiClient from '../services/api'
import useSiteSettings from '../hooks/useSiteSettings'
import defaultDoctors from '../data/defaultDoctors'
import defaultTestimonials from '../data/defaultTestimonials'

const services = [
  { title: 'General Checkup', icon: 'Care', text: 'Routine wellness exams, blood pressure checks, lifestyle guidance, and early screening support for every age group.' },
  { title: 'Dental Care', icon: 'Smile', text: 'Preventive cleanings, oral exams, restorative planning, and ongoing education to support healthy teeth and gums.' },
  { title: 'Diagnostics', icon: 'Lab', text: 'Timely test coordination, accurate reporting, and doctor-led interpretation to help patients move forward confidently.' },
  { title: 'Emergency Care', icon: 'Urgent', text: 'Fast-response urgent assessment for minor emergencies, sudden symptoms, and priority same-day medical attention.' }
]

const highlights = [
  {
    title: 'Patient-First Experience',
    text: 'From the front desk to follow-up care, we focus on clear communication, respectful treatment, and comfortable visits.'
  },
  {
    title: 'Modern Clinic Environment',
    text: 'Our spaces are designed to feel organized, calm, and welcoming, with attention to cleanliness, privacy, and convenience.'
  },
  {
    title: 'Coordinated Care',
    text: 'Doctors, nurses, and support staff work together so patients receive guidance that is consistent, practical, and easy to follow.'
  }
]

const processSteps = [
  'Book your preferred service online or by phone.',
  'Meet our care team for assessment, consultation, and recommendations.',
  'Receive clear next steps, prescriptions, referrals, and follow-up support.'
]

const trustItems = [
  '24/7 Support',
  'Qualified Doctors',
  'Modern Facilities'
]

const heroImage =
  'https://res.cloudinary.com/dmiicgsbq/image/upload/v1776733603/glqjocb9ahg3wo2wkqk0.png'

const mapTestimonials = (items = []) =>
  items.map((item) => ({
    name: item.name,
    message: item.message || item.feedback
  }))

const mapDoctors = (items = []) =>
  items.map((item) => ({
    name: item.name,
    specialty: item.specialty,
    experience: item.experience,
    imageUrl: item.imageUrl || item.image,
    summary: item.summary,
    achievements: item.achievements || []
  }))

const HomePage = () => {
  const [galleryItems, setGalleryItems] = useState([])
  const [blogPosts, setBlogPosts] = useState([])
  const [faqs, setFaqs] = useState([])
  const [cmsTestimonials, setCmsTestimonials] = useState([])
  const [cmsDoctors, setCmsDoctors] = useState([])
  const [selectedBlogPost, setSelectedBlogPost] = useState(null)
  const [openFaqId, setOpenFaqId] = useState(null)
  const settings = useSiteSettings()

  useEffect(() => {
    const loadContent = async () => {
      try {
        const [galleryResponse, blogResponse, faqResponse, testimonialResponse, doctorResponse] = await Promise.all([
          apiClient.get('/api/cms/gallery'),
          apiClient.get('/api/cms/blog'),
          apiClient.get('/api/cms/faqs'),
          apiClient.get('/api/cms/testimonials'),
          apiClient.get('/api/cms/doctors')
        ])

        setGalleryItems(galleryResponse.data.slice(0, 6))
        setBlogPosts(blogResponse.data.slice(0, 3))
        setFaqs(faqResponse.data.slice(0, 5))
        setCmsTestimonials(mapTestimonials(testimonialResponse.data))
        setCmsDoctors(mapDoctors(doctorResponse.data))
      } catch (error) {
        console.error('Error loading homepage CMS content:', error)
      }
    }

    loadContent()
  }, [])

  useEffect(() => {
    if (!selectedBlogPost) return undefined

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setSelectedBlogPost(null)
      }
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', handleEscape)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleEscape)
    }
  }, [selectedBlogPost])

  const allTestimonials = [...defaultTestimonials, ...cmsTestimonials]
  const allDoctors = [...defaultDoctors, ...cmsDoctors]
  const displayFaqs = faqs.length > 0
    ? faqs
    : [
        { _id: 'fallback-1', question: 'How do I book an appointment?', answer: 'Choose your preferred service, date, and time, then send your request online.' },
        { _id: 'fallback-2', question: 'What are your clinic hours?', answer: `${settings.contactHoursWeekdays} ${settings.contactHoursSaturday}` },
        { _id: 'fallback-3', question: 'Do you accept insurance?', answer: 'Please contact our front desk for current coverage and guidance.' }
      ]

  return (
    <div className="home-page">
      <SEO
        title="Home"
        description={`Welcome to ${settings.siteName}. Quality healthcare with compassion for cardiology, pediatrics, dental care, and more.`}
        keywords="medical clinic, healthcare, doctors, cardiology, pediatrics, dental care, appointments"
        url="https://harmonymedical.com"
      />

      <section className="home-landing-panel">
        <div className="home-landing-stage">
          <div className="home-landing-copy">
            <span className="home-landing-kicker">Trusted Family Care</span>
            <p className="home-landing-topline">Your Health,</p>
            <h1 className="home-landing-title">Our Priority</h1>
            <h2 className="home-landing-subtitle">Book an Appointment Easily</h2>
            <p className="home-landing-description">
              Schedule your visit with our experienced doctors in just a few clicks.
            </p>
            <a href="/appointment" className="btn btn-primary btn-lg home-landing-button">
              Book Appointment
            </a>
          </div>

          <div className="home-landing-image-wrap">
            <div className="home-landing-glow home-landing-glow-one"></div>
            <div className="home-landing-glow home-landing-glow-two"></div>
            <img src={heroImage} alt="Smiling female doctor" className="home-landing-image" />
          </div>
        </div>

        <div className="home-landing-trustbar">
          {trustItems.map((item) => (
            <div key={item} className="home-landing-trustitem">
              <span className="home-landing-trusticon" aria-hidden="true"></span>
              <span>{item}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="py-5">
        <div className="container">
          <div className="editorial-heading-block">
            <span className="editorial-kicker">Care Experience</span>
            <h2 className="editorial-title">A clinic visit designed to feel calm, clear, and well guided.</h2>
            <p className="editorial-lead">
              These highlights introduce the values patients notice first, from communication and comfort to how your care stays coordinated after the visit.
            </p>
          </div>
          <div className="row g-4">
            {highlights.map((item) => (
              <div key={item.title} className="col-md-4">
                <div className="card h-100 border-0 shadow-sm p-4 home-soft-theme-card">
                  <h3 className="h5">{item.title}</h3>
                  <p className="text-muted mb-0">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-5">
        <div className="container">
          <div className="section-divider-wave"></div>
          <div className="home-theme-heading">
            <span className="editorial-kicker">Medical Team</span>
            <h2 className="mb-0">Doctor Highlights</h2>
            <p>
              Meet the doctors who guide consultations, treatment decisions, and follow-up care across our clinic.
            </p>
          </div>
          <div className="row g-4">
            {allDoctors.map((doc, idx) => (
              <div key={`${doc.name}-${idx}`} className="col-md-4">
                <div className="card h-100 border-0 shadow-sm overflow-hidden home-doctor-highlight-card">
                  <div className="home-doctor-highlight-image-wrap">
                    <img
                      src={doc.imageUrl}
                      className="card-img-top home-doctor-highlight-image"
                      alt={doc.name}
                    />
                  </div>
                  <div className="card-body home-doctor-highlight-body">
                    <h5 className="card-title">{doc.name}</h5>
                    <p className="card-text text-muted">{doc.specialty}</p>
                    <p className="card-text">{doc.summary}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-5 bg-light">
        <div className="container">
          <div className="editorial-band">
            <div className="home-theme-heading">
              <span className="editorial-kicker">Treatments</span>
              <h2 className="mb-0">Services Preview</h2>
              <p>
                Explore some of the core services patients can book for preventive care, diagnosis, and ongoing treatment support.
              </p>
            </div>
            <div className="row g-4">
              {services.map((service, idx) => (
                <div key={idx} className="col-md-3">
                  <div className="card h-100 border-0 shadow-sm p-3 text-center home-soft-theme-card">
                    <div className="display-6 fw-semibold text-primary mb-3">{service.icon}</div>
                    <h5>{service.title}</h5>
                    <p className="text-muted">{service.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 bg-light">
        <div className="container">
          <div className="section-divider-wave align-right"></div>
          <div className="row g-4 align-items-center editorial-section-soft">
            <div className="col-lg-6">
              <span className="editorial-kicker">Visit Journey</span>
              <h2 className="mb-3">What Patients Can Expect</h2>
              <p className="text-muted">
                We believe good healthcare should feel approachable. Our team explains findings in simple language, discusses treatment options clearly,
                and helps patients understand what to do before, during, and after their visit.
              </p>
              <ul className="mb-0">
                {processSteps.map((step) => (
                  <li key={step} className="mb-2">{step}</li>
                ))}
              </ul>
            </div>
            <div className="col-lg-6">
              <div className="card border-0 shadow-sm p-4 home-soft-theme-card">
                <h3 className="h5 mb-3">Why Families Choose Us</h3>
                <p className="mb-2">Extended consultation support for complex cases.</p>
                <p className="mb-2">Friendly assistance with appointments and follow-up reminders.</p>
                <p className="mb-2">Coordinated referrals and ongoing care planning.</p>
                <p className="mb-0">A clean, organized clinic designed to make visits easier for children, adults, and seniors.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5">
        <div className="container">
          <div className="section-divider-wave"></div>
          <div className="home-theme-heading">
            <span className="editorial-kicker">Patient Trust</span>
            <h2 className="mb-0">Testimonials</h2>
            <p>
              Read what patients and families have shared about the care, attention, and support they received during their visits.
            </p>
          </div>
          <div className="row g-4">
            {allTestimonials.map((item, idx) => (
              <div key={`${item.name}-${idx}`} className="col-md-4">
                <div className="card h-100 border-0 shadow-sm p-4 home-testimonial-theme-card">
                  <p className="mb-3">{item.message}</p>
                  <p className="h6 mb-0">{item.name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {galleryItems.length > 0 && (
        <section className="py-5 bg-light">
          <div className="container">
            <div className="editorial-band">
              <div className="home-theme-heading">
                <span className="editorial-kicker">Inside The Clinic</span>
                <h2 className="mb-0">Gallery</h2>
                <p>
                  Take a closer look at our clinic spaces, treatment areas, and the environment we have created for comfortable care.
                </p>
              </div>
              <div className="row g-4">
                {galleryItems.map((item) => (
                  <div key={item._id} className="col-md-4">
                    <div className="card h-100 border-0 shadow-sm overflow-hidden home-soft-theme-card">
                      <img
                        src={item.imageUrl}
                        alt={item.title || 'Clinic gallery'}
                        style={{ width: '100%', height: '220px', objectFit: 'cover' }}
                      />
                      <div className="card-body">
                        <h3 className="h5">{item.title || 'Clinic Update'}</h3>
                        {item.description && <p className="text-muted mb-0">{item.description}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {blogPosts.length > 0 && (
        <section className="py-5">
          <div className="container">
            <div className="section-divider-wave align-right"></div>
            <div className="home-theme-heading">
              <span className="editorial-kicker">Health Articles</span>
              <h2 className="mb-0">Latest Blog Posts</h2>
              <p>
                Stay informed with practical articles on prevention, treatment, recovery, and everyday health decisions.
              </p>
            </div>
            <div className="row g-4">
              {blogPosts.map((post) => (
                <div key={post._id} className="col-md-4">
                  <div className="card h-100 border-0 shadow-sm p-4 home-soft-theme-card">
                    <h3 className="h5">{post.title}</h3>
                    {post.excerpt && <p className="text-muted">{post.excerpt}</p>}
                    <p className="mb-3">{post.content?.length > 180 ? `${post.content.slice(0, 180)}...` : post.content}</p>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm align-self-start"
                      onClick={() => setSelectedBlogPost(post)}
                    >
                      More
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {selectedBlogPost && (
        <div
          className="blog-dialog-backdrop"
          role="presentation"
          onClick={() => setSelectedBlogPost(null)}
        >
          <div
            className="blog-dialog card border-0 shadow"
            role="dialog"
            aria-modal="true"
            aria-labelledby="blog-dialog-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="btn-close blog-dialog-close"
              aria-label="Close blog post"
              onClick={() => setSelectedBlogPost(null)}
            ></button>
            <div className="card-body p-4 p-md-5">
              <h3 id="blog-dialog-title" className="mb-3">{selectedBlogPost.title}</h3>
              {selectedBlogPost.excerpt && <p className="text-muted mb-3">{selectedBlogPost.excerpt}</p>}
              <div className="blog-dialog-content">
                {selectedBlogPost.content?.split('\n').map((paragraph, index) => (
                  <p key={`${selectedBlogPost._id}-${index}`} className="mb-3">
                    {paragraph}
                  </p>
                ))}
              </div>
              <button
                type="button"
                className="btn btn-primary mt-2"
                onClick={() => setSelectedBlogPost(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="py-5 bg-light">
        <div className="container">
          <div className="editorial-band">
            <div className="home-theme-heading home-faq-theme-heading">
              <span className="editorial-kicker">Helpful Answers</span>
              <h2 className="mb-0">Frequently Asked Questions</h2>
            </div>
            <div className="row g-3">
              {displayFaqs.map((item) => {
                const isOpen = openFaqId === item._id

                return (
                  <div className="col-12" key={item._id}>
                    <div className={`home-faq-theme-row${isOpen ? ' is-open' : ''}`}>
                      <button
                        type="button"
                        className="home-faq-toggle"
                        onClick={() => setOpenFaqId(isOpen ? null : item._id)}
                        aria-expanded={isOpen}
                      >
                        <div className="home-faq-theme-left">
                          <span className="home-faq-theme-dot" aria-hidden="true"></span>
                          <div>
                            <h3>{item.question}</h3>
                            {isOpen && <p>{item.answer}</p>}
                          </div>
                        </div>
                        <span className={`home-faq-theme-arrow${isOpen ? ' is-open' : ''}`} aria-hidden="true">{'>'}</span>
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 home-contact-theme-wrap">
        <div className="container">
          <div className="home-contact-theme-band">
            <div className="home-contact-theme-title">Need Help? Contact Us</div>
            <div className="row g-4">
              <div className="col-md-4">
                <div className="home-contact-theme-item">
                  <span className="home-contact-theme-icon" aria-hidden="true">☎</span>
                  <h5>Call Us</h5>
                  <p>{settings.contactPhone}</p>
                </div>
              </div>
              <div className="col-md-4">
                <div className="home-contact-theme-item">
                  <span className="home-contact-theme-icon" aria-hidden="true">✉</span>
                  <h5>Email Us</h5>
                  <p>{settings.contactEmail}</p>
                </div>
              </div>
              <div className="col-md-4">
                <div className="home-contact-theme-item">
                  <span className="home-contact-theme-icon" aria-hidden="true">⌖</span>
                  <h5>Find Us</h5>
                  <p>{settings.contactAddress}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default HomePage
